import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const DISMISS_KEY = "ca_pwa_install_dismissed_until";

export const PwaInstallBanner = () => {
  const [evt, setEvt] = useState(null);
  const [dismissed, setDismissed] = useState(true);

  // Whether the banner is currently allowed to show
  const refreshDismissal = () => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      setDismissed(Date.now() < until);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    refreshDismissal();

    const handler = (e) => {
      e.preventDefault();
      setEvt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      setEvt(null);
      setDismissed(true);
    };
    window.addEventListener("appinstalled", installed);

    // Engagement hook: any feature can dispatch ca:pwa-suggest to force-show
    // the banner after a meaningful action (e.g. lectura completed, vidente granted).
    const suggest = () => {
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
      setDismissed(false);
    };
    window.addEventListener("ca:pwa-suggest", suggest);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
      window.removeEventListener("ca:pwa-suggest", suggest);
    };
  }, []);

  // Engagement: when an admin grants Vidente Virtual (SSE notification),
  // also surface the install prompt.
  useRealtimeEvents({
    notification: (data) => {
      const cat = data?.category;
      if (cat === "vidente") {
        try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
        setDismissed(false);
      }
    },
  });

  const install = async () => {
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch { /* noop */ }
    setEvt(null);
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    } catch { /* noop */ }
    setDismissed(true);
  };

  if (dismissed) return null;

  // Detect iOS Safari which never fires beforeinstallprompt
  const isIosSafari = (() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    const isStandalone = window.navigator.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)").matches;
    return isIos && isSafari && !isStandalone;
  })();

  // Show banner if either: native prompt available, OR iOS Safari (manual instructions)
  if (!evt && !isIosSafari) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] max-w-md w-[92%] sm:w-auto bg-gradient-to-r from-[#0a0612] via-[#1a0d22] to-[#0a0612] border border-[#d4af37]/50 rounded-2xl shadow-[0_0_40px_rgba(212,175,55,0.35)] px-4 py-3 flex items-center gap-3"
    >
      <div className="w-10 h-10 rounded-full bg-[#d4af37]/20 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
        <Download className="w-5 h-5 text-[#d4af37]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-semibold leading-tight">Instalar CrisAngel Astral</div>
        <div className="text-xs text-neutral-400 leading-tight mt-0.5">
          {isIosSafari
            ? "Toca Compartir y luego \"Añadir a pantalla de inicio\"."
            : "Tenlo en tu pantalla de inicio, listo en un toque."}
        </div>
      </div>
      {!isIosSafari && (
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="btn-gold rounded-full px-4 py-2 text-xs uppercase tracking-widest font-semibold whitespace-nowrap"
        >
          Instalar
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Ocultar"
        className="p-1.5 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
